import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfirmationDialogComponent,
  HeaderFooterService,
  NavigationBreadcrumbExpansion,
  NavigationBreadcrumbSelection,
  PRESETS,
  SnackbarService,
} from '@shared/ui';
import { Analyst, IAssetItem } from '@analysis/domain';
import { AuthService, FeaturesService } from '@telespot/web-core';
import { AssetUtils } from '@telespot/sdk';
import { LoggerService } from '@telespot/shared/logger/feature/util';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { NewClassDialogComponent } from '../dialogs/new-class-dialog/new-class-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import OpenSeadragon from 'openseadragon';
import { ProtocolStore, PipelineStore, StepStore, LabelStore, MethodTypeStore } from '@telespot/protocols/ui';
import { MicroscopeImageFacade } from './state';
import { AI_ANALYSIS_READY, AI_ANALYSIS_SAVE_BLOCKED_I18N, SampleAiAnalysisProgressStore } from './state';
import {
  AssetStore,
  MicroLayoutStore,
  NavigationStore,
  FindingStore,
  RoiStore,
  AnalysisStateStore,
  AnalystStore,
  RoiChangeStore,
  RoiStatsStore,
  MosaicPaginationStore,
} from '../../stores';
import { OverlayData } from '@shared/ui';
import {
  BreadcrumbNavigationEffects,
  MicroRoiFeatureStore,
  RoiStatsEffects,
  RoiRestoreEffects,
  RoiLabelingEffects,
  RoiRevertEffects,
  MicroSyncEffects,
  MicroReviewEffects,
  MicroContextMenuEffects,
  MicroLabelSelectionEffects,
  MicroFindingEffects,
  MicroRoiDrawingEffects,
} from '../../effects';
import { DziViewerComponent } from '@shared/ui';
import { ViewerMode, DrawingMode, ETabContext } from '../../types/viewer.types';
import { DOWNLOAD_ASSETS_DIALOG_COMPONENT } from '../../tokens/component.tokens';
import { CanComponentDeactivate, DownloadService } from '@telespot/shared/util';

@Component({
  selector: 'ts-microscope-image',
  templateUrl: './microscope-image.component.html',
  styleUrls: ['./microscope-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    MicroscopeImageFacade,
    NavigationStore,
    BreadcrumbNavigationEffects,
    SampleAiAnalysisProgressStore,
    AssetStore,
    MicroLayoutStore,
    ProtocolStore,
    PipelineStore,
    StepStore,
    LabelStore,
    MethodTypeStore,
    FindingStore,
    RoiStore,
    RoiStatsStore,
    MosaicPaginationStore,
    AnalysisStateStore,
    AnalystStore,
    MicroRoiFeatureStore,
    RoiChangeStore,
    RoiStatsEffects,
    RoiRestoreEffects,
    RoiLabelingEffects,
    RoiRevertEffects,
    MicroSyncEffects,
    MicroReviewEffects,
    MicroContextMenuEffects,
    MicroLabelSelectionEffects,
    MicroFindingEffects,
    MicroRoiDrawingEffects,
  ],
})
export class MicroscopeImageComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  //#region Parameters

  private _showMobileAnalysisPanelB$ = new BehaviorSubject<boolean>(true);
  private _destroy$ = new Subject<void>();

  private _fallbackViewerMode: ViewerMode;
  private _dragPosition: OpenSeadragon.Point;
  private _contextMenuHandler: ((event: MouseEvent) => void) | null = null;
  private _spacebarPressed = false;

  @ViewChild(DziViewerComponent) dziViewer: DziViewerComponent;
  @ViewChild('contextMenu') contextMenuRef: ElementRef<HTMLElement>;
  @ViewChild('thumbMenuTrigger') thumbMenuTrigger: MatMenuTrigger;
  @ViewChild('rulerTrigger') rulerTrigger?: MatMenuTrigger;
  @ViewChild('filtersTrigger') filtersTrigger?: MatMenuTrigger;

  /** Per-thumbnail context menu (favourite / bookmark / download) state: the right-clicked strip
   *  item and the cursor position the menu anchors to. */
  public thumbMenuItem: IAssetItem | null = null;
  public thumbMenuX = 0;
  public thumbMenuY = 0;

  public readonly showMobileAnalysisPanel$ = this._showMobileAnalysisPanelB$.asObservable();
  public readonly aiPollingExhausted$ = this.sampleAiProgressStore.pollingExhausted$;

  public readonly layout$: Observable<'mobile' | 'web'> = this.breakpoint.observe(['(min-width: 600px)']).pipe(
    map((result) => result.matches),
    startWith(!(this.platform.ANDROID || this.platform.IOS)),
    map<boolean, 'web' | 'mobile'>((web) => (web ? 'web' : 'mobile')),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public noShowCopyDialog = false;
  public showFabs = true;

  public emptySet = new Set<string>();

  // Exposed for the inlined viewer-tools actions in the top bar's center zone.
  public readonly ViewerMode = ViewerMode;
  public readonly DrawingMode = DrawingMode;

  //#endregion

  //#region Constructor

  constructor(
    public readonly authService: AuthService,
    public readonly featureService: FeaturesService,
    public readonly headerFooterService: HeaderFooterService,
    public readonly router: Router,
    public readonly dialog: MatDialog,
    public readonly platform: Platform,
    public readonly route: ActivatedRoute,
    public readonly logger: LoggerService,
    public readonly snackBar: MatSnackBar,
    public readonly breakpoint: BreakpointObserver,
    public readonly micro: MicroscopeImageFacade,
    private readonly snackbarService: SnackbarService,
    private readonly sampleAiProgressStore: SampleAiAnalysisProgressStore,
    private readonly downloadService: DownloadService,
    @Inject(DOWNLOAD_ASSETS_DIALOG_COMPONENT) private readonly downloadAssetsDialog: Type<unknown>
  ) {
    this.headerFooterService.toggleHeaderAndFooter(false);
  }

  //#endregion

  //#region Life Cycle Hooks

  ngOnInit(): void {
    this.micro.saveBlockedByAiAnalysis$.pipe(takeUntil(this._destroy$)).subscribe(() =>
      this.snackbarService.showWithBoldLead(AI_ANALYSIS_SAVE_BLOCKED_I18N.title, AI_ANALYSIS_SAVE_BLOCKED_I18N.body, {
        duration: 12_000,
      })
    );

    this.sampleAiProgressStore.aiAnalysisCompleted$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.snackbarService.show(AI_ANALYSIS_READY, { duration: 6000 }));

    // Keep the analysis panel (AI / Analyst) in sync with the selected analyst's
    // Two distinct cases handled by the same subscription:
    //   - First emission of a valid type: auto-OPEN the matching panel ONLY when
    //     the URL has `historyId` (deep-link / reload of someone else's analysis).
    //     Without a deep-link the panel stays closed, matching the prior UX.
    //   - Subsequent type changes (e.g., sample navigation falls back to current
    //     user because the previously-selected algorithm has no analysis in the
    //     destination sample): SWITCH to the new panel, but only if a panel is
    //     currently open — otherwise we'd re-open something the user just closed.
    let isFirstTypeEmission = true;
    this.micro.selectedAnalyst$
      .pipe(
        map((analyst) => analyst?.type),
        filter((type): type is 'user' | 'algorithm' => !!type),
        distinctUntilChanged(),
        withLatestFrom(this.micro.tabActiveContext$, this.micro.historyId$),
        takeUntil(this._destroy$)
      )
      .subscribe(([type, currentTabContext, urlHistoryId]) => {
        const targetTab = type === 'algorithm' ? ETabContext.AI : ETabContext.ANALYST;
        const isDeepLinkOpen = isFirstTypeEmission && !!urlHistoryId;
        const isOpenPanelMismatch = !isFirstTypeEmission && currentTabContext !== undefined;
        isFirstTypeEmission = false;
        if ((isDeepLinkOpen || isOpenPanelMismatch) && currentTabContext !== targetTab) {
          this.micro.setTabContext(targetTab);
        }
      });

    this.micro.allOptions$.pipe(takeUntil(this._destroy$), distinctUntilChanged()).subscribe((options) => {
      this.micro.initializeBoxDimensions(Object.values(options));
    });

    this.micro.contextMenuVisible$.pipe(takeUntil(this._destroy$)).subscribe((visible) => {
      if (visible && !this._contextMenuHandler) {
        this._contextMenuHandler = (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        };
        document.addEventListener('contextmenu', this._contextMenuHandler, { capture: true });
      } else if (!visible && this._contextMenuHandler) {
        document.removeEventListener('contextmenu', this._contextMenuHandler, { capture: true });
        this._contextMenuHandler = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.headerFooterService.toggleHeaderAndFooter(true);

    this.micro.saveProgress();

    if (this._contextMenuHandler) {
      document.removeEventListener('contextmenu', this._contextMenuHandler, { capture: true });
      this._contextMenuHandler = null;
    }

    this._destroy$.next();
    this._destroy$.complete();
  }

  //#endregion

  public onToogleActiveMode(mode: ViewerMode): void {
    this.micro.setViewerMode(mode);
    if (mode === ViewerMode.drawing) this.micro.deselectRois();
  }

  //#region Public Methods

  public saveAssetData() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, PRESETS.DIALOG_SAVE_ASSET_DATA);

    dialogRef.afterClosed().subscribe((option: string) => {
      if (option !== 'accept') return this.micro.discardDataChanges();

      return this.micro.saveAssetData();
    });
  }

  /** Breadcrumb commit. PUSHES a history entry so browser Back returns to the sample the
   *  user came from; only the facade's url reconciliation replaces. */
  public navigate(caseId: string, sampleId: string) {
    this.router.navigate([`analyze/cases/${caseId}/images/${sampleId}`], {
      queryParams: {},
    });
  }

  /**
   * The breadcrumb commits at its two leaf levels; every level above them expands instead.
   *
   * - Sample: the destination case comes from the selection PATH rather than the open case,
   *   because a cascaded sample can belong to another case.
   * - Analyst: a pick that came through a cascade carries a sample in its path — that is a
   *   location change, committed as sample + analyst in one navigation. A pick straight from
   *   the analyst chip has no sample in its path and only switches the analyst on the open sample.
   */
  public onBreadcrumbSelect(
    selection: NavigationBreadcrumbSelection,
    currentCaseId: string | undefined,
    meOption?: Analyst | null,
    analysts?: Analyst[] | null
  ): void {
    if (selection.levelKey === 'sample') {
      const caseId = this.stepFromPath(selection, 'case') ?? currentCaseId;
      this.onSelectSample(selection.optionId, caseId);
      return;
    }

    if (selection.levelKey === 'analyst') {
      const sampleId = this.stepFromPath(selection, 'sample');
      if (sampleId) {
        const caseId = this.stepFromPath(selection, 'case') ?? currentCaseId;
        if (caseId) this.micro.openSampleAsAnalyst(caseId, sampleId, selection.optionId);
        return;
      }

      const analyst = [meOption, ...(analysts ?? [])].find((candidate) => candidate?.id === selection.optionId);
      if (analyst) this.micro.selectAnalyst(analyst);
    }
  }

  private stepFromPath(selection: NavigationBreadcrumbSelection, levelKey: string): string | undefined {
    return selection.path?.find((step) => step.levelKey === levelKey)?.optionId;
  }

  public onSelectSample(sampleId: string, currentCaseId: string | undefined): void {
    if (!sampleId || !currentCaseId) return;
    // Route by the target sample's real asset type so jumping to a video sample
    // opens the video viewer instead of staying on the images route.
    this.micro.navigateToSample(currentCaseId, sampleId);
  }

  /** Analyst-level dropdown opened: lazily load the open sample's analysts and their states. */
  public onBreadcrumbLevelOpened(levelKey: string, sampleId: string | undefined, caseId: string | undefined): void {
    if (levelKey !== 'analyst' || !sampleId || !caseId) return;
    this.micro.loadAnalysts(sampleId);
    this.micro.loadAnalysisStates(sampleId, caseId);
  }

  /** A cascading option was opened: load the level below it. */
  public onBreadcrumbOptionExpanded(expansion: NavigationBreadcrumbExpansion): void {
    if (expansion.levelKey === 'workspace') this.micro.loadCasesForWorkspace(expansion.optionId);
    if (expansion.levelKey === 'case') this.micro.loadSamplesForCase(expansion.optionId);
    if (expansion.levelKey === 'sample') this.micro.loadAnalystsForSampleId(expansion.optionId);
  }

  public navigateToUserAnalysis(isOwn: boolean, historyId: string): void {
    // Read `asset` from the URL bar (not from `route.snapshot.queryParams`),
    // because `initializeSelectedAssetFromURL` writes it via
    // `location.replaceState`, which doesn't update the router's stored state.
    // Full replace (no `queryParamsHandling: 'merge'`) so the param order is
    // fixed by insertion order: `asset` first, `historyId` second.
    const asset = new URLSearchParams(window.location.search).get('asset');
    const queryParams: Record<string, string> = {};
    if (asset) queryParams.asset = asset;
    if (!isOwn && historyId) queryParams.historyId = historyId;
    this.router.navigate([], { queryParams });
  }

  public getImageFiltersApplied(imageFilters) {
    return imageFilters.some((f) => f.value !== 0);
  }

  public onSave() {
    this.micro.save();
  }

  public onAcceptCompleteAnalysis() {
    this.micro.acceptCompleteAnalysis();
  }

  public onDiscardChanges() {
    this.micro.openDiscardChangesDialog();
  }

  public onExit() {
    this.micro.exit();
  }

  public onDownloadAssets(imageFormat: boolean, refStripItems: IAssetItem[], sampleName: string) {
    this.dialog.open(this.downloadAssetsDialog, {
      hasBackdrop: true,
      disableClose: true,
      width: '800px',
      data: {
        assets: refStripItems.map((item) => {
          return {
            id: item.id,
            thumbnail: item.thumbnail,
            selected: false,
            index: item.index,
            assetFile: item.url,
            data: { hspan: item?.hspan },
          };
        }),
        sampleName,
        imageFormat,
      },
    });
  }

  public onToggleAnalysisView(view: string) {
    if (view !== 'mosaic') return;

    combineLatest([this.micro.caseId$, this.micro.sampleId$, this.micro.historyId$])
      .pipe(take(1))
      .subscribe(([caseId, sampleId, historyId]) => {
        if (!caseId || !sampleId) return;

        const queryParams: any = {};
        if (historyId) queryParams['historyId'] = historyId;

        const mosaicRoute = `analyze/cases/${caseId}/mosaics/${sampleId}`;

        this.router.navigate([mosaicRoute], { queryParams: queryParams });
      });
  }

  public onNewLabel() {
    const dialogRef = this.dialog.open(NewClassDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      //TODO: Pending include custom label functionality
    });
  }

  public onAnalystChange(analyst: Analyst) {
    const isOwn = this.authService.currentUser.id === analyst.id;
    return this.navigateToUserAnalysis(isOwn, analyst.historyId);
  }

  public onDownloadAsset() {
    this.snackBar.open('Wait until download completes', null, {
      duration: 2000,
    });
  }

  public async downloadOriginalImage(asset: IAssetItem): Promise<void> {
    if (!asset?.url) return;
    this.snackBar.open('Wait until download completes', null, { duration: 2000 });
    try {
      await this.downloadService.downloadFile(asset.url, this.getDownloadFileName(asset, asset.index));
    } catch (err) {
      this.logger?.error(`[download] failed to download original image`, err?.message);
      this.snackBar.open('Download failed', null, { duration: 3000 });
    }
  }

  /** RESET the ruler back to its initial state and close the ruler popover. */
  public onRulerReset(): void {
    this.micro.resetRuler();
    this.rulerTrigger?.closeMenu();
  }

  /** RESET the image filters back to their initial state and close the filters popover. */
  public onFiltersReset(): void {
    this.micro.resetImageFilters();
    this.filtersTrigger?.closeMenu();
  }

  /** Filename for the single-asset download link in the top bar. Mirrors the helper that used to
   *  live in `ts-sample-analysis-tools`, now that those actions are inlined into the bar. */
  public getDownloadFileName(asset: IAssetItem, index: number): string {
    // The per-thumbnail menu binds this before an item is selected, so guard against a null asset.
    if (!asset) return '';
    const assetIndex = (index + 1).toString();
    const MAX_ZEROS_PAD_START = 4;
    const hspanPrefix = asset?.data?.hSpan ? `hspan_${asset?.data?.hSpan}_` : '';
    return `${assetIndex.padStart(MAX_ZEROS_PAD_START, '0')}_img_${hspanPrefix}${asset.assetFile}`;
  }

  public onDownloadAssetClick(event: Event): void {
    if (!this.featureService.canUse('downloadAvailable')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.onDownloadAsset();
  }

  /** Opens the per-thumbnail actions menu (favourite / bookmark / download) at the cursor when the
   *  user right-clicks a reference-strip thumbnail. */
  public onThumbnailContextMenu({ item, x, y }: { item: IAssetItem; x: number; y: number }): void {
    this.thumbMenuItem = item;
    this.thumbMenuX = x;
    this.thumbMenuY = y;
    // Reopen cleanly if a menu is already showing for another thumbnail.
    this.thumbMenuTrigger?.closeMenu();
    this.thumbMenuTrigger?.openMenu();
  }

  public onCenterImage() {
    this.dziViewer['viewer'].viewport.goHome();
  }

  // Zoom controls, surfaced in the top bar (the floating zoom widget was removed). Same
  // multiplicative steps the old `ts-osd-zoom-controller` used.
  private zoomBy(factor: number): void {
    const viewport = this.dziViewer?.['viewer']?.viewport;
    if (!viewport) return;
    const newZoom = viewport.getZoom() * factor;
    if (newZoom >= viewport.getMinZoom() && newZoom <= viewport.getMaxZoom()) viewport.zoomTo(newZoom);
  }

  public onZoomIn(): void {
    this.zoomBy(1.2);
  }

  public onZoomOut(): void {
    this.zoomBy(0.8);
  }

  /** Whether the reference strip is expanded; the minimap rides above it and follows it down when
   *  the strip collapses (so it doesn't hang in the middle of the viewer). */
  public refStripVisible = true;

  /** Show/hide the OpenSeadragon navigator (minimap). Toggled from the top bar's "more" menu. */
  public navigatorVisible = true;
  public toggleNavigator(): void {
    this.navigatorVisible = !this.navigatorVisible;
    const navigatorEl = this.dziViewer?.['viewer']?.navigator?.element as HTMLElement | undefined;
    if (navigatorEl) navigatorEl.style.display = this.navigatorVisible ? '' : 'none';
  }

  public onEscape(pressed: boolean) {
    if (!pressed) return;

    this.onToogleActiveMode(ViewerMode.idle);
    this.micro.deselectRois();
    this.micro.clearSelectedLabels();
    this.micro.closeContextMenu();
  }

  public onCtrlMeta(pressed: boolean, isViewMode: boolean, viewerMode: ViewerMode) {
    if (isViewMode) return;

    if (!pressed) {
      this.onToogleActiveMode(this._fallbackViewerMode);
      this.micro.deselectRois();
      this.micro.closeContextMenu();
      return;
    }

    this._fallbackViewerMode = viewerMode;

    this.onToogleActiveMode(ViewerMode.selecting);
  }

  public onSpace(pressed: boolean, viewerMode: ViewerMode) {
    if (pressed) {
      this._spacebarPressed = true;
      this._fallbackViewerMode = viewerMode;
      this.onToogleActiveMode(ViewerMode.idle);
    } else {
      this._spacebarPressed = false;
      this.onToogleActiveMode(this._fallbackViewerMode);
    }
  }

  public onDelete(roiIds: Set<string>) {
    this.micro.removeAllLabels(roiIds);
    this.micro.clearSelectedLabels();
  }

  public onMouseDown(event: MouseEvent, viewerMode: ViewerMode) {
    this._dragPosition = new OpenSeadragon.Point(event.pageX, event.pageY);

    if (!this._spacebarPressed) {
      this._fallbackViewerMode = viewerMode;
    }

    this.onToogleActiveMode(ViewerMode.idle);
  }

  public onMouseUp() {
    if (!this._spacebarPressed) {
      this.onToogleActiveMode(this._fallbackViewerMode);
    }
    this._dragPosition = null;
  }

  public onMouseMove(event: MouseEvent) {
    if (!this._dragPosition) return;

    const delta = new OpenSeadragon.Point(event.pageX, event.pageY).minus(this._dragPosition);

    const offset = this.dziViewer['viewer'].viewport.deltaPointsFromPixels(delta.negate());

    this.dziViewer['viewer'].viewport.panBy(offset);

    this._dragPosition = new OpenSeadragon.Point(event.pageX, event.pageY);
  }

  public onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.micro.showContextMenu(event);
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (this.contextMenuRef?.nativeElement?.contains(target)) {
      return;
    }

    this.micro.closeContextMenu();
  }

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.micro.hasUnsyncedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  public canDeactivate(): Observable<boolean> {
    return this.micro.canDeactivate();
  }

  public onOverlayUpdated(overlay: OverlayData) {
    this.micro.updateRoiPosition({ overlay });
  }

  public onOverlayAdded(overlay: OverlayData, selectedLabels: unknown[]) {
    if (!selectedLabels?.length) {
      this.micro.openSnackbar('analysis.select_label_before_drawing');
      return;
    }

    const roi = AssetUtils.makeIntegerCoords(overlay);
    this.micro.addRois({ overlay: roi });
  }

  //#endregion

  //#region Mobile Panel Methods

  public toggleAsset() {
    this._showMobileAnalysisPanelB$.next(true);
  }

  public closeMobileAnalysisPanel() {
    this._showMobileAnalysisPanelB$.next(false);
  }
  //#endregion
}
